import React, { useMemo } from 'react'
import { Box, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AppTextField } from 'src/components/AppTextField'
//The location filter is hidden for this version. Will be available once the locations are clarified
//import { SelectField } from 'src/components/SelectField'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ReportTemplateLocation, ReportTemplateStatus } from 'src/enums'
import { getStatusConfig } from '../FormSchemaAccordionHeader'
import { invalidateReportTemplate, useChangeReportTemplateStatus } from 'src/api/report-template'
import { toast } from 'react-toastify'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { IReportTemplateWarningDto } from 'src/generated-types/report-template'

interface ILocationOption {
  id: ReportTemplateLocation
  name: string
}

export interface IReportSettingsPanelProps {
  templateName: string
  locations: number[]
  status?: ReportTemplateStatus
  onTemplateNameChange: (name: string) => void
  onLocationsChange: (locations: number[]) => void
  isEditMode?: boolean
  templateId?: number
  customerId?: number
  formSchemaVersionId?: number
  onUpgrade?: () => void
  isUpgradeLoading?: boolean
  warnings?: IReportTemplateWarningDto
  formSchemaId?: EntityId
}

export const ReportSettingsPanel: React.FC<IReportSettingsPanelProps> = ({
  templateName,
  //locations,
  status,
  onTemplateNameChange,
  //onLocationsChange,
  isEditMode,
  templateId,
  customerId,
  formSchemaVersionId,
  onUpgrade,
  isUpgradeLoading = false,
  warnings,
  formSchemaId,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])
  const STATUS_CONFIG = useMemo(() => getStatusConfig(theme), [theme])

  const { closeAllDialogs, showConfirmDialog } = useDialogCtx()

  const { mutateAsync: changeStatus } = useChangeReportTemplateStatus(customerId as EntityId, templateId as EntityId)

  const handleStatusChange = async (newStatus: ReportTemplateStatus) => {
    await changeStatus(
      { status: newStatus, formSchemaId: formSchemaId as EntityId },
      {
        onSuccess: () => {
          invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId as EntityId)
          invalidateReportTemplate.getFormSchemaReportTemplates(customerId as EntityId)
          invalidateReportTemplate.getReportTemplateById(customerId as EntityId, templateId as EntityId)
          invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
            customerId as EntityId,
            (formSchemaVersionId as EntityId) ?? 0,
          )
          toast.success(translate('status-update-success', { ns: 'dynamic-report' }))
          closeAllDialogs()
        },
        onError: () => {
          toast.error(translate('status-update-error', { ns: 'dynamic-report' }))
        },
      },
    )
  }

  const handleStatusChangeAction = async (newStatus: ReportTemplateStatus) => {
    if (!isEditMode) return
    if (!customerId || !templateId) return
    showConfirmDialog(
      translate('deactivate-report-template-title', { ns: 'dynamic-report' }),
      translate('deactivate-report-template-message', {
        ns: 'dynamic-report',
      }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await handleStatusChange(newStatus)
      },
    )
  }

  const handleActivateReportTemplate = async (newStatus: ReportTemplateStatus) => {
    if (!isEditMode) return
    if (!customerId || !templateId) return
    await handleStatusChange(newStatus)
  }

  //The location filter is hidden for this version. Will be available once the locations are clarified
  // const locationOptions: ILocationOption[] = useMemo(
  //   () =>
  //     Object.keys(ReportTemplateLocation)
  //       .filter(key => isNaN(Number(key)))
  //       .map(key => ({
  //         id: ReportTemplateLocation[key as keyof typeof ReportTemplateLocation],
  //         name: translate(key, { ns: 'dynamic-report' }),
  //       })),
  //   [translate],
  // )

  // const selectedLocations = useMemo(
  //   () => locationOptions.filter(opt => locations.includes(opt.id as number)),
  //   [locationOptions, locations],
  // )

  // const handleLocationsChange = (_: React.SyntheticEvent, value: unknown) => {
  //   const values = value as ILocationOption[]
  //   const selected = Array.isArray(values) ? values : values ? [values] : []
  //   onLocationsChange(selected.map(v => v.id as number))
  // }

  const statusConfig = status != null ? STATUS_CONFIG[status] : null

  return (
    <Box display="flex" flexDirection="column" gap={2.5} pt={0.5}>
      {statusConfig && (
        <StatusChip bgColor={statusConfig.bgColor} chipColor={statusConfig.color}>
          {translate(statusConfig.labelKey, { ns: 'dynamic-report' }).toUpperCase()}
        </StatusChip>
      )}
      <AppTextField
        label={translate('template-name', { ns: 'dynamic-report' })}
        value={templateName}
        onChange={e => onTemplateNameChange(e.target.value)}
        fullWidth
      />
      {/* The location filter is hidden for this version. Will be available once the locations are clarified */}
      {/* <SelectField<ILocationOption>
        data={locationOptions}
        label={translate('locations', { ns: 'dynamic-report' })}
        multiple
        value={selectedLocations}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={handleLocationsChange}
        fullWidth
        size="small"
      /> */}
      <ActionButtonsContainer>
        {STATUS_CONFIG &&
          warnings &&
          warnings?.removedInLatest.length > 0 &&
          !warnings?.isLatestVersion &&
          status !== ReportTemplateStatus.Inactive && (
            <GridActionButton
              buttonText={translate('upgrade-report', { ns: 'dynamic-report' })}
              onClick={onUpgrade}
              disabled={!isEditMode}
              textColor={STATUS_CONFIG[1].color}
              fullWidth
              loading={isUpgradeLoading}
            />
          )}
        {status !== ReportTemplateStatus.Inactive && (
          <ActionButton
            buttonText={translate('deactivate', { ns: 'dynamic-report' })}
            onClick={() => handleStatusChangeAction(ReportTemplateStatus.Inactive)}
            disabled={!isEditMode}
            textColor={theme.palette.error.main}
            fullWidth
          />
        )}
        {status === ReportTemplateStatus.Inactive && (
          <GridActionButton
            buttonText={translate('activate', { ns: 'dynamic-report' })}
            onClick={() => handleActivateReportTemplate(ReportTemplateStatus.Active)}
            disabled={!isEditMode}
            border={2}
            textColor={theme.palette.success.main}
            fullWidth
          />
        )}
      </ActionButtonsContainer>
    </Box>
  )
}

const StatusChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'bgColor' && prop !== 'chipColor',
})<{ bgColor: string; chipColor: string }>(({ bgColor, chipColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 10px',
  borderRadius: '4px',
  backgroundColor: bgColor,
  color: chipColor,
  fontWeight: 700,
  fontSize: '14px',
  letterSpacing: '0.05em',
  width: '100%',
}))

const ActionButtonsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

const ActionButton = styled(GridActionButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.error.main}`,
}))
